import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useInstantSearch, useSearchBox } from 'react-instantsearch';

import { useTracking } from '@change-corgi/core/react/tracking';

import { useLandingPage, useResetFiltersOnSamePageNavigation } from 'src/app/pages/search/shared/hooks';
import { useFocusFallback } from 'src/app/shared/hooks/focus';
import { useUpdateQueryString } from 'src/app/shared/hooks/location';

type Result = ModelHookResult<
	{
		isLoading: boolean;
		inputRef: React.RefObject<HTMLInputElement>;
		inputValue: string;
	},
	{
		setQuery: (newQuery: string) => void;
		clearQuery: () => void;
		submitSearch: () => void;
	}
>;

// eslint-disable-next-line max-lines-per-function
export function useCustomSearchBox(): Result {
	const {
		data: { showLandingPage },
	} = useLandingPage();

	const track = useTracking();

	const { query, refine, clear } = useSearchBox();
	const { status, results } = useInstantSearch();
	const [inputValue, setInputValue] = useState(query);
	const inputRef = useRef(null);
	const updateQueryParams = useUpdateQueryString();

	useFocusFallback(inputRef, ({ focus, container }) => focus(container, { preventScroll: true }));
	useResetFiltersOnSamePageNavigation({
		resetFilters: () => {
			setInputValue('');
		},
	});

	const isLoading = useMemo(
		() => results.__isArtificial || status === 'loading' || status === 'stalled',
		[status, results],
	);

	const setQuery = (newQuery: string) => {
		setInputValue(newQuery);
	};

	useEffect(() => {
		if (query && !inputValue) {
			clear();
		}
	}, [query, inputValue, clear]);

	useEffect(() => {
		// This clears the input when the user goes from the landing page to the search page
		// but only if there is not a query
		if (!showLandingPage && !query) setInputValue('');
	}, [showLandingPage, query]);

	return {
		data: {
			inputRef,
			isLoading,
			inputValue,
		},
		actions: {
			submitSearch: useCallback(() => {
				if (inputValue) {
					refine(inputValue);
				} else {
					// This is used to navigate to the search page when the user clicks on the search button
					// without entering a query
					updateQueryParams({ q: '' });
				}
				void track('search', { q: inputValue, context: 'search_term_entered' });
			}, [inputValue, refine, track, updateQueryParams]),
			setQuery,
			clearQuery: useCallback(() => {
				setInputValue('');
				clear();
				void track('search', { q: query, context: 'clear_search_button' });
			}, [clear, query, track]),
		},
	};
}
